import React, { forwardRef } from 'react';
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Box,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface CustomTextFieldProps extends Omit<MuiTextFieldProps, 'variant'> {
  error?: boolean;
  helperText?: string;
  showPassword?: boolean;
  handleClickShowPassword?: () => void;
  turnOff?: boolean;
  customVariant?: 'money' | 'password' | 'date';
  value?: string;
  required?: boolean;
  compactError?: boolean;
}

/**
 * A custom text field component for authentication forms.
 *
 * @prop {boolean} [error] - Whether the text field should display an error state.
 * @prop {string} [helperText] - The helper text to display if the text field is in an error state.
 * @prop {boolean} [showPassword] - Whether the password should be visible.
 * @prop {() => void} [handleClickShowPassword] - The function to toggle password visibility.
 * @prop {boolean} [turnOff=false] - Whether the text field should be disabled.
 * @prop {'money' | 'password' | 'date'} [customVariant] - The variant type of the text field.
 * @prop {string} [value] - The current value of the text field.
 * @prop {boolean} [required] - Whether the text field is required.
 * @prop {boolean} [compactError] - Whether error spacing should be compacted.
 * @returns {JSX.Element} The rendered TextField component.
 */
const TextFieldAuth = forwardRef<HTMLInputElement, CustomTextFieldProps>(
  (
    {
      error,
      helperText,
      showPassword,
      handleClickShowPassword,
      turnOff = false,
      customVariant,
      value,
      required = false,
      compactError,
      sx,
      ...props
    },
    ref,
  ) => {
    const borderColor = error ? '#E53E3E' : '#FFFFFF';
    const textColor = '#FFFFFF';

    const combinedSx = {
      width: '100%',
      '& .MuiFilledInput-root': {
        backgroundColor: 'transparent',
        borderRadius: 0,
        borderBottom: `1px solid ${borderColor}`,
        '&:hover': {
          backgroundColor: 'transparent',
          borderBottomColor: borderColor,
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
          borderBottomColor: borderColor,
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
        },
        position: 'relative',
        paddingLeft: customVariant === 'password' ? '40px' : '12px',
      },
      '& .MuiInputBase-input': {
        color: textColor,
        textAlign: 'right',
        direction: 'rtl',
        '&:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0px 1000px transparent inset`,
          WebkitTextFillColor: textColor,
          transition: 'background-color 5000s ease-in-out 0s',
          background: 'transparent',
        },
      },
      '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:hover:before, & .MuiFilledInput-underline:after':
        {
          borderBottomColor: `${borderColor} !important`,
        },
      '& .MuiIconButton-root': {
        position: 'absolute',
        left: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: textColor,
        zIndex: 1,
      },
      ...sx,
    };

    return (
      <Box sx={{ position: 'relative', width: '100%', direction: 'rtl' }}>
        {/* Поле ввода */}
        <MuiTextField
          ref={ref}
          type={
            customVariant === 'password'
              ? showPassword
                ? 'text'
                : 'password'
              : 'text'
          }
          variant="filled"
          InputProps={{
            disableUnderline: true,
            sx: { direction: 'rtl', ...combinedSx },
          }}
          InputLabelProps={{
            sx: {
              color: textColor,
              textAlign: 'right',
              right: 18,
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              left: 'auto',
              transformOrigin: 'top right',
              direction: 'rtl',
              '&.Mui-focused': { color: textColor },
            },
          }}
          label={
            required ? (
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <Typography component="span">{props.label}</Typography>
                <Typography
                  component="span"
                  sx={{ color: '#E53E3E', marginRight: '4px' }}
                >
                  *
                </Typography>
              </Box>
            ) : (
              props.label
            )
          }
          value={value}
          sx={combinedSx}
          disabled={turnOff}
          error={!!error}
          required={required}
          fullWidth
          {...props}
        />

        {/* Абсолютный текст ошибки */}
        {error && helperText && (
          <Box
            sx={{
              position: 'absolute',
              bottom:
                helperText.length > 40
                  ? compactError
                    ? '-25px'
                    : '-35px'
                  : compactError
                    ? '-10px'
                    : '-20px',
              right: 0,
              color: '#E53E3E',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '4px',
              zIndex: 1000,
              lineHeight: '1.2',
              maxWidth: '100%',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: '13px', marginTop: '2px' }} />
            <Typography fontWeight="300" fontSize="13px" fontFamily="Heebo">
              {helperText}
            </Typography>
          </Box>
        )}

        {/* Динамический отступ */}
        <Box
          sx={{
            height:
              error && helperText
                ? helperText.length > 40
                  ? compactError
                    ? '10px'
                    : '10px'
                  : compactError
                    ? '10px'
                    : '10px'
                : '0px',
          }}
        />
      </Box>
    );
  },
);

export default TextFieldAuth;
